import { useState } from "react";
import { LogOut, Crown, User as UserIcon, Settings, Loader2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import api from "@/lib/api";
import { toast } from "sonner";

const statusPill = (user) => {
  if (user?.has_active_subscription) {
    if (user.subscription_status === "trialing") {
      const days = user.trial_end
        ? Math.max(0, Math.ceil((new Date(user.trial_end).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
        : 0;
      return { text: `Trial · ${days}d left`, color: "text-[#39FF14]", dot: "bg-[#39FF14]" };
    }
    return { text: "Unlimited", color: "text-[#39FF14]", dot: "bg-[#39FF14]" };
  }
  const used = user?.free_analyses_used ?? 0;
  const limit = user?.free_analyses_limit ?? 3;
  return { text: `${Math.max(0, limit - used)} free left`, color: "text-zinc-400", dot: "bg-[#FF2D55]" };
};

export default function UserMenu({ user, onLogout, onUpgrade }) {
  const [portalLoading, setPortalLoading] = useState(false);
  const pill = statusPill(user);

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const res = await api.post("/stripe/portal", { origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error("Couldn't open billing", {
        description: err?.response?.data?.detail || err?.message || "Try again.",
      });
      setPortalLoading(false);
    }
  };

  const initial = (user?.name || user?.email || "?").trim().charAt(0).toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="user-menu-trigger"
          className="flex items-center gap-2 bg-[#0A0A0A] border border-white/10 hover:border-white/20 rounded-full pl-1 pr-3 py-1 transition-colors group"
        >
          {user?.picture ? (
            <img
              src={user.picture}
              alt=""
              className="w-7 h-7 rounded-full"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="w-7 h-7 rounded-full bg-[#FF2D55]/20 border border-[#FF2D55]/30 flex items-center justify-center text-[11px] text-[#FF2D55] font-bold font-display">
              {initial}
            </div>
          )}
          <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-mono font-semibold">
            <span className={`w-1.5 h-1.5 rounded-full ${pill.dot} animate-pulse`} />
            <span className={pill.color} data-testid="user-status-pill">{pill.text}</span>
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-[#0A0A0A] border border-white/10 text-white min-w-[220px]"
      >
        <DropdownMenuLabel className="font-body">
          <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono">signed in as</div>
          <div className="text-sm font-semibold truncate" title={user?.email}>{user?.name || user?.email}</div>
          <div className="text-[11px] text-zinc-500 truncate" title={user?.email}>{user?.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/10" />
        {user?.has_active_subscription ? (
          <DropdownMenuItem
            onClick={handlePortal}
            data-testid="manage-billing-menu-item"
            className="cursor-pointer focus:bg-white/10 focus:text-white"
          >
            {portalLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Settings className="w-4 h-4 mr-2" />}
            Manage billing
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onClick={onUpgrade}
            data-testid="upgrade-menu-item"
            className="cursor-pointer focus:bg-white/10 focus:text-white"
          >
            <Crown className="w-4 h-4 mr-2 text-[#FF2D55]" />
            Upgrade to Unlimited
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem
          onClick={onLogout}
          data-testid="logout-menu-item"
          className="cursor-pointer text-red-400 focus:bg-red-500/10 focus:text-red-300"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
